/** Authored SVG geometry and poses. All coordinates use a 100 × 100 canvas. */
export type BotState =
    | 'idle' | 'curious' | 'thinking' | 'working' | 'waiting' | 'blocked' | 'done';

export type BotVariant =
    | 'blob' | 'circle' | 'squircle' | 'hexagon' | 'pill' | 'triangle' | 'teardrop' | 'cloud';

// Matching cubic segments let GSAP interpolate the organic outline without a
// morph plugin. These paths are original; no reference artwork is embedded.
export const SHAPES = [
    'M50 4C77 1 96 20 97 48C99 77 79 96 50 96C21 98 3 78 4 50C1 22 22 4 50 4Z',
    'M50 3C80 7 97 24 95 51C94 80 72 98 46 96C18 94 2 72 5 46C7 18 25 1 50 3Z',
    'M50 5C76 0 95 18 96 46C100 73 82 94 53 97C25 98 5 82 3 54C0 28 21 9 50 5Z',
    'M50 4C79 2 95 24 96 52C96 78 74 98 47 96C20 97 3 76 4 48C2 20 23 3 50 4Z',
];

export const HEAD_SHAPES: Record<BotVariant, string> = {
    blob: SHAPES[0],
    circle: 'M50 3A47 47 0 1 0 50 97A47 47 0 1 0 50 3Z',
    squircle: 'M29 4H71Q96 4 96 29V71Q96 96 71 96H29Q4 96 4 71V29Q4 4 29 4Z',
    hexagon: 'M46 5Q50 3 54 5L89 26Q93 28 93 33V67Q93 72 89 74L54 95Q50 97 46 95L11 74Q7 72 7 67V33Q7 28 11 26Z',
    pill: 'M30 20H70A30 30 0 0 1 70 80H30A30 30 0 0 1 30 20Z',
    triangle: 'M43 11Q50 0 57 11L91 75Q101 92 81 92H19Q-1 92 9 75Z',
    teardrop: 'M43 10Q50-2 57 10L81 45Q99 71 88 85Q76 97 50 97Q24 97 12 85Q1 71 19 45Z',
    cloud: 'M24 77C9 77 4 60 16 52C11 37 27 27 39 34C45 20 67 22 72 35C88 32 96 47 86 58C95 68 86 80 76 77Z',
};

// Eyes share four cubic segments. The curved done pose reads as relief even
// without motion or color, rather than relying on a transient success badge.
export const EYE_SHAPES = {
    open: 'M-4-7C-4-12 4-12 4-7C4-3 4 3 4 7C4 12-4 12-4 7C-4 3-4-3-4-7Z',
    smile: 'M-6 2C-5-5 5-5 6 2C7 5 5 6 4 3C2 0-2 0-4 3C-5 6-7 5-6 2Z',
};

export interface Timing { ease: string; duration: number }

export const TIMING: Record<BotState, Timing> = {
    idle: { ease: 'power2.out', duration: 0.32 },
    curious: { ease: 'back.out(1.3)', duration: 0.3 },
    thinking: { ease: 'power3.out', duration: 0.36 },
    working: { ease: 'power3.out', duration: 0.24 },
    waiting: { ease: 'power2.out', duration: 0.38 },
    blocked: { ease: 'power4.out', duration: 0.2 },
    done: { ease: 'back.out(1.2)', duration: 0.36 },
};

export interface Pose {
    x: number; y: number; rotation: number;
    head: number; sx: number; sy: number;
    sats: [number, number][];
    eye: { sx: number; sy: number; tilt: number; lDy: number; rDy: number; dx: number };
}

export const POSE: Record<BotState, Pose> = {
    idle: { x: 0, y: 0, rotation: 0, head: 1, sx: 1, sy: 1, sats: [], eye: { sx: 1, sy: 1, tilt: 0, lDy: 0, rDy: 0, dx: 0 } },
    curious: { x: 0, y: -1, rotation: -4, head: 1, sx: 0.98, sy: 1.02, sats: [], eye: { sx: 1.04, sy: 1.08, tilt: -4, lDy: -0.02, rDy: 0.01, dx: 0.015 } },
    thinking: { x: 0, y: 0, rotation: 0, head: 0.5, sx: 1, sy: 1, sats: [[-38, 0.85], [38, 0.65]], eye: { sx: 0.92, sy: 0.9, tilt: 0, lDy: 0, rDy: 0, dx: 0 } },
    working: { x: 1, y: 0, rotation: 4, head: 1, sx: 1.04, sy: 0.96, sats: [], eye: { sx: 1.25, sy: 0.56, tilt: -3, lDy: -0.012, rDy: 0.018, dx: 0.025 } },
    waiting: { x: -1, y: 1, rotation: -5, head: 1, sx: 0.96, sy: 1, sats: [], eye: { sx: 1.15, sy: 0.34, tilt: -8, lDy: 0.008, rDy: 0.025, dx: -0.045 } },
    blocked: { x: 0, y: 1, rotation: 0, head: 1, sx: 1.06, sy: 0.93, sats: [], eye: { sx: 0.9, sy: 0.52, tilt: 16, lDy: -0.015, rDy: -0.015, dx: 0 } },
    done: { x: 0, y: -1, rotation: 0, head: 1, sx: 1, sy: 1, sats: [], eye: { sx: 1, sy: 1, tilt: 0, lDy: -0.01, rDy: -0.01, dx: 0 } },
};

export const TEMPO: Record<BotState, { morph: number; breath: number; blink: number }> = {
    idle: { morph: 3.4, breath: 2.8, blink: 3.4 },
    curious: { morph: 2.8, breath: 1.8, blink: 2.8 },
    thinking: { morph: 1.6, breath: 1.4, blink: 2.1 },
    working: { morph: 1.1, breath: 0.56, blink: 2.2 },
    waiting: { morph: 3.6, breath: 3.2, blink: 4.2 },
    blocked: { morph: 0, breath: 0, blink: 1.9 },
    done: { morph: 3.2, breath: 2.6, blink: 4 },
};
